/**
 * Page-focused traffic view pieces (`/traffic/page?path=…`).
 *
 * Lives beside RumPlaceholders.tsx rather than inside it — the KPI
 * shape here (page-traffic row: views / entries / exits) differs from
 * the site KPI payload that `RumKpiRow` resolves, and RumPlaceholders
 * is already at capacity.
 */
import { BreakdownIcon } from '@/components/primitives/BreakdownIcon';
import { StatusChip } from '@/components/primitives/MetricTile';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type MetricBand,
  bandText,
  clsBand,
  fcpBand,
  formatCls,
  formatMs,
  inpBand,
  lcpBand,
  ttfbBand,
} from '@/lib/format';
import { useBilling } from '@/lib/queries/billing';
import {
  type PixelPageSessionRow,
  type PixelPageTrafficRow,
  usePagePixelSessions,
} from '@/lib/queries/pixel';
import { cn } from '@/lib/utils';
import { UsersThree } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

// ─── KPI row ────────────────────────────────────────────────────────

function formatCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString();
}

function formatDurationSeconds(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m ${s}s`;
}

const PAGE_TRAFFIC_KPIS: Array<{
  label: string;
  hint: string;
  resolve: (r: PixelPageTrafficRow) => string;
}> = [
  {
    label: 'Pageviews',
    hint: 'Total renders of this page',
    resolve: (r) => formatCount(r.pageviews),
  },
  {
    label: 'Unique visitors',
    hint: 'People who viewed it',
    resolve: (r) => formatCount(r.visitors),
  },
  { label: 'Sessions', hint: 'Visits that included it', resolve: (r) => formatCount(r.sessions) },
  { label: 'Entries', hint: 'Sessions that started here', resolve: (r) => formatCount(r.entries) },
  { label: 'Exits', hint: 'Sessions that ended here', resolve: (r) => formatCount(r.exits) },
  {
    label: 'Avg engagement',
    hint: 'Active time per view',
    resolve: (r) => formatDurationSeconds(r.avg_engagement_ms),
  },
];

/** Six-tile KPI strip for one page — visual sibling of `RumKpiRow`
 *  (same tile chrome), but resolving the page-traffic row shape. */
export function PageTrafficKpiRow({
  data,
}: {
  /** undefined = loading, null = resolved with no data. */
  data: PixelPageTrafficRow | null | undefined;
}): ReactElement {
  const hasData = !!data && (data.pageviews > 0 || data.sessions > 0);
  return (
    <div
      key={hasData ? 'live' : 'placeholder'}
      className={cn(
        'grid gap-px overflow-hidden rounded-xl border border-[var(--color-line)] bg-[var(--color-line)] [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]',
        hasData && 'animate-in fade-in duration-300',
      )}
    >
      {PAGE_TRAFFIC_KPIS.map((k) => {
        const value = data ? k.resolve(data) : '—';
        return (
          <div key={k.label} className="relative overflow-hidden bg-[var(--color-paper)] p-4">
            <div className="relative flex min-w-0 flex-col gap-2">
              <span
                className="truncate text-xs font-medium text-[var(--color-mute)]"
                title={k.hint}
              >
                {k.label}
              </span>
              <span
                className={cn(
                  'text-3xl font-bold leading-[1.1] tabular-nums',
                  hasData ? 'text-[var(--color-ink)]' : 'text-[var(--color-mute)]/70',
                )}
              >
                {value}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Sessions card ──────────────────────────────────────────────────

/** ClickHouse DateTime strings are UTC without a zone marker. */
export function parseClickHouseUtc(ts: string): Date {
  return new Date(`${ts.replace(' ', 'T')}Z`);
}

function formatRelative(ts: string): string {
  const then = parseClickHouseUtc(ts).getTime();
  if (Number.isNaN(then)) return ts;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

export function formatSessionDuration(startedAt: string, endedAt: string): string {
  const ms = parseClickHouseUtc(endedAt).getTime() - parseClickHouseUtc(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/**
 * Compact RUM readout — the web vitals one visitor actually experienced
 * (from that session's pixel beacons), values coloured by the standard
 * CWV bands. Renders null when the session reported none, so callers can
 * collapse the whole slot.
 *
 * The sessions list passes the three headline vitals; the session
 * timeline also passes `ttfb`/`fcp`, which it has per page lifecycle.
 * Omitted metrics drop out of the readout entirely.
 */
export function SessionVitals({
  lcp,
  cls,
  inp,
  ttfb,
  fcp,
}: {
  lcp: number | null;
  cls: number | null;
  inp: number | null;
  ttfb?: number | null;
  fcp?: number | null;
}): ReactElement | null {
  const vitals = [
    { label: 'LCP', value: lcp, text: formatMs(lcp), band: lcpBand(lcp) },
    { label: 'CLS', value: cls, text: formatCls(cls), band: clsBand(cls) },
    { label: 'INP', value: inp, text: formatMs(inp), band: inpBand(inp) },
    { label: 'TTFB', value: ttfb ?? null, text: formatMs(ttfb), band: ttfbBand(ttfb) },
    { label: 'FCP', value: fcp ?? null, text: formatMs(fcp), band: fcpBand(fcp) },
  ].filter((v) => v.value != null);
  if (vitals.length === 0) return null;
  return (
    // Wraps because the session timeline passes all five metrics, which
    // overflow a phone's width on one line.
    <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5 tabular-nums">
      {vitals.map((v) => (
        <span key={v.label} className="inline-flex items-baseline gap-1">
          <span className="text-[10px] font-medium text-[var(--color-mute)]">{v.label}</span>
          <span className={cn('text-[11px] font-semibold', bandText(v.band))}>{v.text}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * One-word verdict for a session, replacing the three metrics the list
 * used to print per row.
 *
 * The worst band wins, which is how a Core Web Vitals assessment works
 * and how a visit is actually remembered — a page that loaded fast and
 * then shifted under the reader's thumb was not a good visit.
 *
 * Deliberately the app's existing Good / Needs work / Poor vocabulary
 * rather than Fast / Slow: CLS measures layout stability, so a session
 * failing only on CLS isn't "slow", it's unpleasant in a different way,
 * and calling it slow would send someone hunting the wrong problem. The
 * exact numbers stay one hover away.
 */
const BAND_RANK: Record<MetricBand, number> = {
  unknown: -1,
  good: 0,
  'needs-improvement': 1,
  poor: 2,
};

function sessionBand(s: PixelPageSessionRow): MetricBand {
  const bands: MetricBand[] = [];
  if (s.lcp != null) bands.push(lcpBand(s.lcp));
  if (s.cls != null) bands.push(clsBand(s.cls));
  if (s.inp != null) bands.push(inpBand(s.inp));
  if (bands.length === 0) return 'unknown';
  return bands.reduce((worst, b) => (BAND_RANK[b] > BAND_RANK[worst] ? b : worst), 'good');
}

/** The numbers the chip is standing in for — kept in the row tooltip so
 *  summarising costs no information. */
function vitalsSummary(s: PixelPageSessionRow): string {
  return [
    s.lcp != null ? `LCP ${formatMs(s.lcp)}` : '',
    s.cls != null ? `CLS ${formatCls(s.cls)}` : '',
    s.inp != null ? `INP ${formatMs(s.inp)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Which visitor id a row can show, and how much it's worth.
 *
 * `profile_uuid` is a real person: a site-scoped hash of the id the
 * customer passed to `cwv.identify()`, stable for as long as that user
 * exists. `device_uuid` always resolves but is a daily-rotating hash of
 * (salt, /24 subnet, coarse UA) — the same visitor gets a new one every
 * UTC midnight, and two people behind one office or carrier subnet on
 * matching phones can collide onto one.
 *
 * They are different claims, so the row marks which it got rather than
 * blending them into an undifferentiated "user id" that reads as
 * identity in both cases. Truncated because the full 16 chars is more
 * hash than anyone reads — the whole value is in the tooltip.
 */
function visitorId(s: PixelPageSessionRow): { id: string; durable: boolean } | null {
  if (s.profile_uuid) return { id: s.profile_uuid.slice(0, 8), durable: true };
  if (s.device_uuid) return { id: s.device_uuid.slice(0, 8), durable: false };
  return null;
}

function visitorTitle(s: PixelPageSessionRow, journey: string): string {
  const who = s.profile_uuid
    ? `Identified user ${s.profile_uuid} — stable across visits`
    : s.device_uuid
      ? `Device ${s.device_uuid} — resets daily, not a durable identity`
      : 'No visitor id on this session';
  return `${who}\n${journey}`;
}

const SESSIONS_PAGE_SIZE = 50;
// The API caps `limit` at 200 — hide "Load more" once we hit it.
const SESSIONS_MAX = 200;

/**
 * Sessions that viewed this page, newest first. Rows are humanised
 * (time · journey · context); the session id itself only appears as
 * truncated monospace secondary text. Each row links into the session
 * timeline, carrying `t` (started_at) so the events query can bound
 * its raw scan.
 */
export function PageSessionsCard({
  siteUuid,
  path,
  days,
}: {
  siteUuid: string | null;
  path: string;
  days: number;
}): ReactElement {
  // "Load more" grows the limit rather than paging offsets — the query
  // re-fetches the full slice (keepPreviousData holds the old rows in
  // place), which keeps this component stateless about row identity.
  const [limit, setLimit] = useState(SESSIONS_PAGE_SIZE);
  const sessions = usePagePixelSessions(siteUuid, { path, days, limit });
  const rows = sessions.data ?? [];
  const canLoadMore = rows.length >= limit && limit < SESSIONS_MAX;
  // Only spend a column on vitals when at least one session reported any.
  const hasVitals = rows.some((s) => s.lcp != null || s.cls != null || s.inp != null);
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center gap-2 border-b border-[var(--color-line-soft)] px-3 py-2.5">
        <UsersThree aria-hidden="true" size={14} className="text-[var(--color-mute)]" />
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Sessions on this page</h3>
      </header>
      {rows.length > 0 ? (
        <>
          {/* A real <table>, not a CSS grid pretending to be one. The
              grid version had to hand-tune seven column widths at two
              breakpoints and still left voids on wide windows; a table
              sizes to its content, and the shared primitive brings the
              header band, row dividers, hover and scroll container that
              were being reimplemented here by hand. Same component the
              sibling Search page uses. */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead title="Identified user where the site calls cwv.identify(); otherwise a daily device hash. Hover a row to see which.">
                  Visitor
                </TableHead>
                <TableHead className="text-right max-sm:hidden">Views</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                {hasVitals && <TableHead className="max-sm:hidden">Experience</TableHead>}
                <TableHead className="max-sm:hidden">Device</TableHead>
                <TableHead className="max-sm:hidden">Channel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <SessionRow key={s.session_uuid} session={s} path={path} showVitals={hasVitals} />
              ))}
            </TableBody>
          </Table>
        </>
      ) : sessions.isPending ? (
        <div className="px-6 py-8 text-center text-xs text-[var(--color-mute)]">
          Loading sessions…
        </div>
      ) : (
        <div className="px-6 py-8 text-center text-xs text-[var(--color-mute)]">
          No sessions viewed this page in this window.
        </div>
      )}
      {canLoadMore && (
        <footer className="flex justify-center border-t border-[var(--color-line-soft)] px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={sessions.isFetching}
            onClick={() => setLimit((n) => Math.min(n + SESSIONS_PAGE_SIZE, SESSIONS_MAX))}
          >
            {sessions.isFetching ? 'Loading…' : 'Load more'}
          </Button>
        </footer>
      )}
    </section>
  );
}

function SessionRow({
  session,
  path,
  showVitals,
}: {
  session: PixelPageSessionRow;
  path: string;
  showVitals: boolean;
}): ReactElement {
  const s = session;
  const startedFull = parseClickHouseUtc(s.started_at).toLocaleString();
  const sameEnds = s.entry_path === s.exit_path;
  const journeyTitle = sameEnds ? s.entry_path : `${s.entry_path} → ${s.exit_path}`;
  const band = sessionBand(s);
  const visitor = visitorId(s);
  return (
    <TableRow className="relative text-xs text-[var(--color-ink-soft)]">
      {/* The anchor lives in the first cell rather than wrapping the row:
          a <tr> can't legally contain one, and a real link keeps
          keyboard focus, middle-click and open-in-new-tab working. The
          `after:` overlay stretches its hit area across the whole row so
          the mouse target is unchanged. */}
      <TableCell className="text-[var(--color-mute)]">
        <Link
          to="/traffic/session/$sessionId"
          params={{ sessionId: s.session_uuid }}
          search={{ t: s.started_at, path }}
          title={startedFull}
          className="after:absolute after:inset-0 after:content-[''] hover:text-[var(--color-ink)]"
        >
          {formatRelative(s.started_at)}
        </Link>
      </TableCell>
      {/* Where they went is the row's tooltip rather than a column of
          its own — on a page-scoped list it repeated the current page
          on most rows. */}
      <TableCell title={visitorTitle(s, journeyTitle)}>
        {visitor ? (
          <span className="flex items-center gap-1.5">
            <VisitorAvatar id={visitor.id} durable={visitor.durable} />
            <span className="font-mono text-[11px]">{visitor.id}</span>
          </span>
        ) : (
          <span className="text-[var(--color-mute)]">—</span>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums max-sm:hidden">{s.pageviews}</TableCell>
      <TableCell
        className="text-right tabular-nums"
        title={`${Math.round(s.engagement_ms / 1000)}s engaged`}
      >
        {formatSessionDuration(s.started_at, s.ended_at)}
      </TableCell>
      {showVitals && (
        <TableCell className="max-sm:hidden" title={vitalsSummary(s) || undefined}>
          {band !== 'unknown' && <StatusChip band={band} />}
        </TableCell>
      )}
      <TableCell
        className="max-sm:hidden"
        title={[s.country_code, s.form_factor, s.browser_family].filter(Boolean).join(' · ')}
      >
        <span className="flex items-center gap-1.5">
          <BreakdownIcon cardKey="geo" dimension="country_code" bucket={s.country_code} />
          <BreakdownIcon cardKey="devices" dimension="form_factor" bucket={s.form_factor} />
          {s.browser_family && <span className="text-[var(--color-mute)]">{s.browser_family}</span>}
        </span>
      </TableCell>
      <TableCell
        className="text-[var(--color-mute)] max-sm:hidden"
        title={[s.channel, s.referrer_host].filter(Boolean).join(' · ') || undefined}
      >
        {s.channel || '—'}
      </TableCell>
    </TableRow>
  );
}

/** Avatar for a visitor id — a stable colour and initial derived from
 *  the hash itself, so the same visitor looks the same on every row they
 *  appear in. That recurrence is the only reason the column earns its
 *  width; a wall of near-identical hashes doesn't read, a repeated
 *  colour does. Muted ring on device ids keeps the identified-vs-
 *  fingerprint distinction the dot used to carry. */
const AVATAR_TONES = [
  'var(--color-accent-brand)',
  'var(--color-success)',
  'var(--color-warning)',
  'var(--color-danger)',
  'var(--color-mute)',
] as const;

function VisitorAvatar({ id, durable }: { id: string; durable: boolean }): ReactElement {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const tone = AVATAR_TONES[h % AVATAR_TONES.length]!;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-full font-medium text-[8px] uppercase',
        !durable && 'opacity-60',
      )}
      style={{ background: `color-mix(in srgb, ${tone} 18%, transparent)`, color: tone }}
    >
      {id.slice(2, 3)}
    </span>
  );
}

// ─── Retention caption ──────────────────────────────────────────────

/**
 * Quiet caption under raw-events-backed surfaces (breakdown cards,
 * sessions) when the selected window exceeds the plan's raw-event
 * retention. The MV-backed KPIs + trend above stay honest at any
 * range; the raw surfaces silently truncate to the retention TTL, so
 * this names the truncation instead of letting the mismatch confuse.
 * Renders nothing when the window fits (or plan limits are unknown).
 */
export function RetentionCaption({ range }: { range: number }): ReactElement | null {
  const billing = useBilling();
  const months = billing.plan_limits.retention_months;
  if (!months || range <= months * 30) return null;
  return (
    <p className="text-xs text-[var(--color-mute)]">
      Source and session detail covers the last {months === 1 ? 'month' : `${months} months`} —
      events are retained that long on your plan. The totals and trend above cover the full window.
    </p>
  );
}
